import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Search, Target, TrendingUp, Rocket, MessageSquare,
  ChevronDown, ChevronUp, Brain, Users, Lightbulb, 
  CheckCircle2, ArrowRight, Award, BookOpen, BarChart3,
  Eye, Heart, Compass, Flag, Mic, Sparkles, GraduationCap,
  Zap, Clock, Flame, MessageCircle, ClipboardCheck, RefreshCw
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { openBookDemo } from '../utils/config';

const FiveDMethodology = ({ 
  onBackToHome, 
  onBlogClick, 
  onCaseStudiesClick, 
  onResourcesClick,
  onCareersClick,
  onCoachesClick,
  onPrivacyClick,
  onTermsClick,
  onShopClick 
}) => {
  const { t, i18n } = useTranslation();
  const [expandedPhase, setExpandedPhase] = useState('discover');
  const [expandedCompetency, setExpandedCompetency] = useState(null);

  const phases = [
    {
      id: 'discover',
      icon: Search,
      color: 'blue',
      gradient: 'from-blue-500 to-blue-600',
      lightBg: 'bg-blue-50',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      ringColor: 'ring-blue-500',
      competencies: [
        { id: 'selfAwareness', icon: Eye },
        { id: 'empathy', icon: Heart },
        { id: 'teamDynamics', icon: Users }
      ]
    },
    {
      id: 'define',
      icon: Target,
      color: 'purple',
      gradient: 'from-purple-500 to-purple-600',
      lightBg: 'bg-purple-50',
      textColor: 'text-purple-600',
      borderColor: 'border-purple-500',
      ringColor: 'ring-purple-500',
      competencies: [
        { id: 'strategicGoals', icon: Flag },
        { id: 'clearCommunication', icon: Mic },
        { id: 'goalOwnership', icon: Users }
      ]
    },
    {
      id: 'develop',
      icon: TrendingUp,
      color: 'green',
      gradient: 'from-green-500 to-green-600',
      lightBg: 'bg-green-50',
      textColor: 'text-green-600',
      borderColor: 'border-green-500',
      ringColor: 'ring-green-500',
      competencies: [
        { id: 'coachingSkills', icon: Compass },
        { id: 'creativity', icon: Sparkles },
        { id: 'continuousLearning', icon: GraduationCap }
      ]
    },
    {
      id: 'drive',
      icon: Rocket,
      color: 'orange',
      gradient: 'from-orange-500 to-orange-600',
      lightBg: 'bg-orange-50',
      textColor: 'text-orange-600',
      borderColor: 'border-orange-500',
      ringColor: 'ring-orange-500',
      competencies: [
        { id: 'actionOrientation', icon: Zap },
        { id: 'followUp', icon: Clock },
        { id: 'motivation', icon: Flame }
      ]
    },
    {
      id: 'debrief',
      icon: MessageSquare,
      color: 'teal',
      gradient: 'from-teal-500 to-teal-600',
      lightBg: 'bg-teal-50',
      textColor: 'text-teal-600',
      borderColor: 'border-teal-500',
      ringColor: 'ring-teal-500',
      competencies: [
        { id: 'feedback', icon: MessageCircle },
        { id: 'selfAssessment', icon: ClipboardCheck },
        { id: 'learningFromProcess', icon: RefreshCw }
      ]
    }
  ];

  const measurementDimensions = [
    { id: 'mindset', icon: Brain },
    { id: 'behavior', icon: Users },
    { id: 'outcome', icon: BarChart3 }
  ];

  const togglePhase = (phaseId) => {
    setExpandedPhase(expandedPhase === phaseId ? null : phaseId);
    setExpandedCompetency(null);
  };

  const toggleCompetency = (compId) => {
    setExpandedCompetency(expandedCompetency === compId ? null : compId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        onBlogClick={onBlogClick}
        onCaseStudiesClick={onCaseStudiesClick}
        onResourcesClick={onResourcesClick}
        onShopClick={onShopClick}
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-300 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Award className="w-4 h-4" />
              {t('fiveD.page.badge')}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('fiveD.page.title')}
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              {t('fiveD.page.subtitle')}
            </p>
            
            {/* 5D Icons Row */}
            <div className="flex justify-center items-center gap-4 md:gap-8 mt-12">
              {phases.map((phase, index) => {
                const Icon = phase.icon;
                return (
                  <div key={phase.id} className="flex items-center">
                    <div 
                      className={`w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br ${phase.gradient} flex items-center justify-center shadow-lg cursor-pointer transform hover:scale-110 transition-transform`}
                      onClick={() => {
                        setExpandedPhase(phase.id);
                        document.getElementById('phases')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <Icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
                    </div>
                    {index < phases.length - 1 && (
                      <ArrowRight className="w-5 h-5 text-white/50 mx-2 hidden md:block" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  {t('fiveD.page.intro.title')}
                </h2>
                <p className="text-gray-600 mb-4">
                  {t('fiveD.page.intro.p1')}
                </p>
                <p className="text-gray-600">
                  {t('fiveD.page.intro.p2')}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  {t('fiveD.page.intro.researchTitle')}
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{t('fiveD.page.intro.research1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{t('fiveD.page.intro.research2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{t('fiveD.page.intro.research3')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Measurement Dimensions */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('fiveD.page.dimensions.title')}
            </h2>
            <p className="text-gray-600">
              {t('fiveD.page.dimensions.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {measurementDimensions.map((dim) => {
              const Icon = dim.icon;
              return (
                <div key={dim.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    {t(`fiveD.page.dimensions.${dim.id}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {t(`fiveD.page.dimensions.${dim.id}.description`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Phases Deep Dive */}
      <section id="phases" className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('fiveD.page.phases.title')}
            </h2>
            <p className="text-gray-600">
              {t('fiveD.page.phases.subtitle')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {phases.map((phase, phaseIndex) => {
              const PhaseIcon = phase.icon;
              const isExpanded = expandedPhase === phase.id;
              
              return (
                <div 
                  key={phase.id}
                  className={`rounded-2xl border-2 overflow-hidden transition-all duration-300 ${
                    isExpanded ? `${phase.borderColor} shadow-lg` : 'border-gray-200'
                  }`}
                >
                  {/* Phase Header */}
                  <button
                    onClick={() => togglePhase(phase.id)}
                    className={`w-full p-6 flex items-center justify-between ${
                      isExpanded ? phase.lightBg : 'bg-white hover:bg-gray-50'
                    } transition-colors`}
                    data-testid={`phase-${phase.id}-toggle`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${phase.gradient} flex items-center justify-center shadow-md`}>
                        <PhaseIcon className="w-7 h-7 text-white" />
                      </div>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${phase.textColor}`}>
                            {t('fiveD.page.phase')} {phaseIndex + 1}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">
                          {t(`fiveD.phases.${phase.id}.title`)}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {t(`fiveD.phases.${phase.id}.subtitle`)}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className={`w-6 h-6 ${phase.textColor}`} />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-gray-400" />
                    )}
                  </button>

                  {/* Phase Content */}
                  {isExpanded && (
                    <div className="p-6 bg-white border-t border-gray-100">
                      <p className="text-gray-600 mb-6">
                        {t(`fiveD.phases.${phase.id}.fullDescription`)}
                      </p>

                      {/* Competencies */}
                      <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Lightbulb className={`w-5 h-5 ${phase.textColor}`} />
                        {t('fiveD.page.competencies')}
                      </h4>
                      
                      <div className="space-y-3">
                        {phase.competencies.map((comp) => {
                          const CompIcon = comp.icon;
                          const isCompExpanded = expandedCompetency === `${phase.id}-${comp.id}`;
                          
                          return (
                            <div 
                              key={comp.id}
                              className={`rounded-xl border overflow-hidden ${
                                isCompExpanded ? `${phase.borderColor} ${phase.lightBg}` : 'border-gray-200'
                              }`}
                            >
                              <button
                                onClick={() => toggleCompetency(`${phase.id}-${comp.id}`)}
                                className="w-full p-4 flex items-center justify-between text-left"
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-lg ${phase.lightBg} flex items-center justify-center`}>
                                    <CompIcon className={`w-5 h-5 ${phase.textColor}`} />
                                  </div>
                                  <span className="font-semibold text-gray-900">
                                    {t(`fiveD.competencies.${comp.id}`)}
                                  </span>
                                </div>
                                {isCompExpanded ? (
                                  <ChevronUp className={`w-5 h-5 ${phase.textColor}`} />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-400" />
                                )}
                              </button>
                              
                              {isCompExpanded && (
                                <div className="px-4 pb-4">
                                  <p className="text-gray-600 text-sm mb-3">
                                    {t(`fiveD.competencyDetails.${comp.id}.definition`)}
                                  </p>
                                  <div className="bg-white rounded-lg p-4 border border-gray-100">
                                    <h5 className="text-sm font-semibold text-gray-900 mb-2">
                                      {t('fiveD.page.observableBehaviors')}
                                    </h5>
                                    <ul className="space-y-2">
                                      {[1, 2, 3].map((num) => (
                                        <li key={num} className="flex items-start gap-2 text-sm text-gray-600">
                                          <CheckCircle2 className={`w-4 h-4 ${phase.textColor} flex-shrink-0 mt-0.5`} />
                                          <span>{t(`fiveD.competencyDetails.${comp.id}.behaviors.b${num}`)}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Assessment Scale */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-purple-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {t('fiveD.page.scale.title')}
            </h2>
            <p className="text-blue-100">
              {t('fiveD.page.scale.subtitle')}
            </p>
          </div>

          <div className="max-w-3xl mx-auto grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">4-5</div>
              <div className="text-lg font-semibold mb-1">{t('fiveD.page.scale.strong')}</div>
              <p className="text-sm text-blue-200">{t('fiveD.page.scale.strongDesc')}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-yellow-400 mb-2">3-3.9</div>
              <div className="text-lg font-semibold mb-1">{t('fiveD.page.scale.improvement')}</div>
              <p className="text-sm text-blue-200">{t('fiveD.page.scale.improvementDesc')}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-red-400 mb-2">0-2.9</div>
              <div className="text-lg font-semibold mb-1">{t('fiveD.page.scale.development')}</div>
              <p className="text-sm text-blue-200">{t('fiveD.page.scale.developmentDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t('fiveD.page.cta.title')}
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            {t('fiveD.page.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={openBookDemo}
              className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-blue-700 transition-colors"
              data-testid="methodology-book-demo"
            >
              {t('nav.bookDemo')}
              <ArrowRight className="w-5 h-5" />
            </button>
            <button 
              onClick={onShopClick}
              className="inline-flex items-center justify-center gap-2 border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-full font-semibold hover:border-blue-600 hover:text-blue-600 transition-colors"
              data-testid="methodology-view-services"
            >
              {t('fiveD.page.cta.viewServices')}
            </button>
          </div>
        </div>
      </section>

      <Footer 
        onBlogClick={onBlogClick}
        onCaseStudiesClick={onCaseStudiesClick}
        onResourcesClick={onResourcesClick}
        onCareersClick={onCareersClick}
        onCoachesClick={onCoachesClick}
        onPrivacyClick={onPrivacyClick}
        onTermsClick={onTermsClick}
        onBackToHome={onBackToHome}
        onShopClick={onShopClick}
      />
    </div>
  );
};

export default FiveDMethodology;
